import React, { ComponentProps } from 'react';

import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContentList, ContentListItem } from '../../../_lib/components/ContentList';
import { ExportDownloadControls, useExportDownload } from '../../../_lib/components/ExportDownloadControls';
import { PersonInformation } from '../../../components/molecules/PersonInformation';
import { ProfileImageSmall } from '../../../types/api/ImageType';
import { ISonURL } from '../../../types/IUrl';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { ContentParagraph, ContentParagraphModifier } from '../../../_lib/components/ContentParagraph';

export type EventParticipant = {
  infoCardUrl: { url: string } | undefined;
  profileLink?: ISonURL;
  profileImageUrl?: ProfileImageSmall;
};

type ParticipatingMembersViewProps = {
  participants: EventParticipant[];
  exportAsCsvUrl: string;
};

function ParticipatingMembers({ participants, exportAsCsvUrl }: ParticipatingMembersViewProps) {
  const { t } = useSonTranslation('EventParticipation');
  const { state, objectUrl, fileNameWithoutExtension, generateExport, fileName } = useExportDownload(
    exportAsCsvUrl,
    'csv'
  );

  if (!participants || participants.length === 0) {
    return (
      <ContentParagraph modifier={ContentParagraphModifier.PADDING}>
        {t('participantListView.headline_zero')}
      </ContentParagraph>
    );
  }

  return (
    <>
      <ContentList>
        {participants.map((participant, index) => (
          <ContentListItem key={participant.profileLink?.url ?? index}>
            <PersonInformation
              infoCardUrl={participant.infoCardUrl}
              profileLink={participant.profileLink}
              profileName={participant.profileLink?.name}
              profileImageUrl={participant.profileImageUrl}
            />
          </ContentListItem>
        ))}
      </ContentList>
      {exportAsCsvUrl && (
        <div className="event-participation__list-export-container">
          <ExportDownloadControls
            buttonLabel={t('participantListView.button.exportAsCsv')}
            fileExtension="csv"
            onGenerate={generateExport}
            fetchState={state}
            objectUrl={objectUrl}
            fileName={fileNameWithoutExtension}
            download={fileName}
          />
        </div>
      )}
    </>
  );
}

export const ParticipatingMembersView = asFetchableView(ParticipatingMembers);
export type ParticipatingMembersView = ComponentProps<typeof ParticipatingMembersView>;
